import { ExpandIcon } from '@mm/icons';
import ArrowIcon, { ARROW_TYPES } from '@mm/icons/general/ArrowIcon';
import classnames from 'classnames';
import React, { useState } from 'react';
import { MoreIcon } from '../../component-icons/MoreIcon/MoreIcon';
import { OnClickElsewhere } from '../OnClickElsewhere/OnClickElsewhere';
import { RequiredPlanBadge } from '../ProBadge';
import ProRibbon from '../ProRibbon/ProRibbon';
import { Props as CollapsibleProps } from './CollapsibleContentContainer';
import './CollapsibleContentContainer.pcss';

type Props = CollapsibleProps & {
  isExpanded: boolean;
  toggleIsExpanded: (isExpanded: boolean) => void;
};

export const CollapsibleContentContainerWithoutLocalState = (props: Props) => {
  const {
    orderNumber,
    containerName,
    childrenContainerClassName,
    isExpanded,
    toggleIsExpanded,
    children,
    proRibbonType,
    proBadgeType,
    onClickOverride,
    className,
    headerClassName,
    disableExpand,
    dropdownMenuContent,
    onDropdownMenuChange,
    useCrossIconStyle,
    useCircleNumberStyle,
    isProcessed,
    hideExpandIcon,
    hideTitleBorder,
    stepperStylingPlatform,
  } = props;

  const expanded = isExpanded && !disableExpand;
  const clickHandler = onClickOverride
    ? () => onClickOverride(() => toggleIsExpanded(!isExpanded))
    : () => toggleIsExpanded(!isExpanded);
  const direction = expanded ? 'top' : 'bottom';
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const stepperStylesByPlatform = () => {
    switch (stepperStylingPlatform) {
      case 'facebook':
        return {
          expanded: 'facebook-expanded',
          collapsed: 'facebook-collapsed',
        };
      case 'facebook-inverted':
        return {
          expanded: 'facebook-inverted-expanded',
          collapsed: 'facebook-inverted-collapsed',
        };
      case 'instagram':
        return {
          expanded: 'instagram-expanded',
          collapsed: 'instagram-collapsed',
        };
      case 'sms':
        return {
          expanded: 'sms-expanded',
          collapsed: 'sms-collapsed',
        };
      case 'webchat':
        return {
          expanded: 'webchat-expanded',
          collapsed: 'webchat-collapsed',
        };
      case 'native_integrations':
        return {
          expanded: 'native-integrations-expanded',
          collapsed: 'native-integrations-collapsed',
        };
      case 'mailchimp':
        return {
          expanded: 'mailchimp-expanded',
          collapsed: 'mailchimp-collapsed',
        };
      case 'webhooks':
        return {
          expanded: 'webhooks-expanded',
          collapsed: 'webhooks-collapsed',
        };
      case 'sold':
        return {
          expanded: 'sold-expanded',
          collapsed: 'sold-collapsed',
        };
      case 'xray':
        return {
          expanded: 'xray-expanded',
          collapsed: 'xray-collapsed',
        };
      default:
        return {
          expanded: '',
          collapsed: '',
        };
    }
  };

  const classList = classnames('collapsible-content-container', className, {
    'collapsible-content-container--hidden': !expanded,
    'collapsible-content-container--cross-icon': useCrossIconStyle,
    'collapsible-content-container--circle-style': useCircleNumberStyle,
    'collapsible-content-container--processed': isProcessed,
    'collapsible-content-container--no-icon': hideExpandIcon,
    'collapsible-content-container--no-border-title': hideTitleBorder,
  });

  const headerClassList = classnames(
    'collapsible-content-container__header',
    headerClassName,
  );

  const toggleDropdownActiveState = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsDropdownActive(!isDropdownActive);
    if (onDropdownMenuChange) onDropdownMenuChange(!isDropdownActive);
  };

  const onDropdownContentClick = (e: React.MouseEvent<HTMLDivElement>) =>
    e.stopPropagation();

  const onOutsideDropdownClick = (e: React.MouseEvent<HTMLDivElement>) =>
    setIsDropdownActive(false);

  const numberClassName = !useCircleNumberStyle
    ? 'collapsible-content-container__number'
    : isExpanded
    ? classnames(
        'collapsible-content-container__number--circle-expanded',
        stepperStylesByPlatform().expanded,
      )
    : classnames(
        'collapsible-content-container__number--circle',
        stepperStylesByPlatform().collapsed,
      );

  const childrednContainerClassList = classnames(
    'collapsible-content-container__dropdown',
    childrenContainerClassName,
  );

  return (
    <div className={classList}>
      <div className={headerClassList} onClick={clickHandler}>
        {useCrossIconStyle && (
          <ExpandIcon
            expand={expanded}
            color={expanded ? '#312D4A' : '#938BB7'}
            className="collapsible-content-container__expand-icon"
          />
        )}
        {orderNumber && (
          <span className={numberClassName}>
            {useCrossIconStyle || useCircleNumberStyle
              ? `${orderNumber}`
              : `${orderNumber}.`}
          </span>
        )}
        {useCircleNumberStyle && (
          <ArrowIcon
            direction={expanded ? 'right' : 'top'}
            color="#312d4a"
            size={15}
            arrowType={ARROW_TYPES.TRIANGLE}
            viewBox="0 0 8 14"
            className="collapsible-content-container__arrow-icon"
          />
        )}
        <span className="collapsible-content-container__title">
          {containerName}

          {proRibbonType && (
            <ProRibbon
              ribbonType={proRibbonType}
              className="collapsible-content-container__pro-icon"
            />
          )}
          {proBadgeType && (
            <RequiredPlanBadge
              billingType={proBadgeType}
              className="collapsible-content-container__pro-icon"
            />
          )}
        </span>

        {dropdownMenuContent && (
          <OnClickElsewhere
            className="collapsible-content-container__dropdown_container"
            handleClick={onOutsideDropdownClick}
          >
            <MoreIcon
              className="collapsible-content-container__more-icon"
              color="#4e5274"
              onClick={toggleDropdownActiveState}
            />

            {isDropdownActive && (
              <div
                className="collapsible-content-container__dropdown_content"
                onClick={onDropdownContentClick}
              >
                {dropdownMenuContent}
              </div>
            )}
          </OnClickElsewhere>
        )}

        {!useCrossIconStyle && !useCircleNumberStyle && (
          <ArrowIcon
            direction={direction}
            color="#4e5274"
            size={15}
            arrowType={ARROW_TYPES.CHEVRON}
            className="collapsible-content-container__arrow-icon"
          />
        )}
      </div>

      {expanded ? (
        <div className={childrednContainerClassList}>{children}</div>
      ) : null}
    </div>
  );
};
